import {
  ActionIcon,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCamera, IconFocus2 } from '@tabler/icons-react';
import { MapEvent } from 'mapbox-gl';
import { ChangeEvent, FC, useCallback, useId, useMemo, useRef } from 'react';
import { MapRef, NavigationControl } from 'react-map-gl';

import mobileClasses from '../../styles/farmer-mobile.module.css';
import mapClasses from '../../styles/harvest-proof-map.module.css';
import { getFreshGeolocationPosition } from '../../hooks/use-geolocation';
import { useDeviceHeading } from '../../hooks/use-device-heading';
import {
  getDistanceToFieldMeters,
  HARVEST_PROOF_MAX_DISTANCE_METERS,
  isCloseEnoughToField,
} from '../../utils/field-proximity';
import { buildFieldsFeatureCollection } from '../../utils/harvest-proof-fields';
import {
  getHarvestProofPhotoInputProps,
  isLikelyLiveCameraCapture,
} from '../../utils/mobile-camera';
import { computeProximityUi } from '../../utils/proximity-ui';
import { DeviceOrientationPrompt } from '../device-orientation-prompt/device-orientation-prompt';
import { ProximityIndicator } from '../proximity-indicator/proximity-indicator';
import { UserPositionMarker } from '../user-position-marker/user-position-marker';

import classes from './field-capture.module.css';

import { HarvestProofAnswerPublic, isHarvestProofAnswerSubmitted } from '@harvest-proof/data-access';
import { PfMap, PlotsLayer, zoomOnFeature } from '@map';

interface FieldCaptureProps {
  answer: HarvestProofAnswerPublic;
  position: GeolocationPosition | null;
  isSubmitting: boolean;
  onCaptureAndSubmit: (args: {
    file: File;
    latitude: number;
    longitude: number;
    deviceHeading: number | null;
  }) => Promise<void>;
}

export const FieldCapture: FC<FieldCaptureProps> = ({
  answer,
  position,
  isSubmitting,
  onCaptureAndSubmit,
}) => {
  const mapRef = useRef<MapRef>(null);
  const isHandlingFileRef = useRef(false);
  const photoInputId = useId();
  const photoInputProps = useMemo(() => getHarvestProofPhotoInputProps(), []);
  const {
    headingRef,
    orientationError,
    startTracking: startHeadingTracking,
    needsPermission: needsOrientationPermission,
    isRequesting: isRequestingOrientation,
  } = useDeviceHeading(true);
  const latitude = position?.coords.latitude;
  const longitude = position?.coords.longitude;

  const proximityMeters = useMemo(() => {
    if (latitude == null || longitude == null) {
      return null;
    }
    return getDistanceToFieldMeters(answer.field, latitude, longitude);
  }, [answer.field, latitude, longitude]);

  const proximityUi = useMemo(
    () => computeProximityUi(proximityMeters),
    [proximityMeters],
  );

  const isAlreadySubmitted = isHarvestProofAnswerSubmitted(answer);
  const hasGps = latitude != null && longitude != null;
  const isCloseEnough = isCloseEnoughToField(answer.field, latitude, longitude);
  const canCapture = hasGps && isCloseEnough && !isSubmitting && !isAlreadySubmitted;

  const fieldData = useMemo(
    () =>
      buildFieldsFeatureCollection([answer], {
        selectedAnswerId: answer.id,
      }),
    [answer],
  );

  const zoomToField = useCallback(() => {
    if (!answer.field || !mapRef.current) {
      return;
    }

    zoomOnFeature({
      map: mapRef.current.getMap(),
      geojson: answer.field,
      padding: 40,
    });
  }, [answer.field]);

  const handleLoad = useCallback(
    (map: MapEvent) => {
      if (!answer.field) {
        return;
      }

      zoomOnFeature({
        map: map.target,
        geojson: answer.field,
        padding: 40,
      });
    },
    [answer.field],
  );

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = '';

    if (!file || isHandlingFileRef.current) {
      return;
    }

    if (!isLikelyLiveCameraCapture(file)) {
      notifications.show({
        color: 'orange',
        message:
          'Prenez une photo maintenant avec l’appareil photo. Les images de la galerie ne sont pas acceptées.',
      });
      return;
    }

    if (latitude == null || longitude == null) {
      notifications.show({
        color: 'orange',
        message: 'Position GPS introuvable. Réactivez la localisation puis réessayez.',
      });
      return;
    }

    let captureLatitude = latitude;
    let captureLongitude = longitude;

    try {
      const freshPosition = await getFreshGeolocationPosition();
      captureLatitude = freshPosition.coords.latitude;
      captureLongitude = freshPosition.coords.longitude;
    } catch {
      notifications.show({
        color: 'orange',
        message:
          'Impossible d’actualiser votre position GPS. Réessayez à l’extérieur ou près d’une fenêtre.',
      });
      return;
    }

    if (!isCloseEnoughToField(answer.field, captureLatitude, captureLongitude)) {
      const meters = getDistanceToFieldMeters(
        answer.field,
        captureLatitude,
        captureLongitude,
      );
      notifications.show({
        color: 'orange',
        message:
          meters === null
            ? 'Vous vous êtes éloigné de la parcelle. Rapprochez-vous puis reprenez la photo.'
            : `Vous êtes à ${Math.round(meters)} m de la parcelle. Rapprochez-vous à moins de ${HARVEST_PROOF_MAX_DISTANCE_METERS} m.`,
      });
      return;
    }

    isHandlingFileRef.current = true;
    try {
      await onCaptureAndSubmit({
        file,
        latitude: captureLatitude,
        longitude: captureLongitude,
        deviceHeading: headingRef.current,
      });
    } finally {
      isHandlingFileRef.current = false;
    }
  };

  return (
    <Stack gap={0}>
      <DeviceOrientationPrompt
        isRequesting={isRequestingOrientation}
        needsPermission={needsOrientationPermission}
        onActivate={startHeadingTracking}
        orientationError={orientationError}
      />
      <Box className={mobileClasses.divider}>
        <Group className={mobileClasses.sectionPad} justify="space-between" wrap="nowrap">
          <Text fw={700} lineClamp={2} size="lg" style={{ flex: 1 }}>
            {answer.field.properties?.name}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Recentrer sur la parcelle">
              <ActionIcon
                aria-label="Recentrer sur la parcelle"
                disabled={isSubmitting}
                onClick={zoomToField}
                size="xl"
                variant="light"
              >
                <IconFocus2 size={22} />
              </ActionIcon>
            </Tooltip>
            <Text fw={600} size="md" style={{ whiteSpace: 'nowrap' }}>
              {answer.field.properties?.area?.toFixed?.(2) ?? '-'} ha
            </Text>
          </Group>
        </Group>
        <Box className={mapClasses.mapWrapperCapture}>
          <PfMap
            classnames={{ container: mapClasses.mapInner }}
            onLoad={handleLoad}
            ref={mapRef}
          >
            <NavigationControl position="top-right" showCompass={false} />
            <PlotsLayer data={fieldData} />
            {hasGps ? (
              <UserPositionMarker latitude={latitude} longitude={longitude} />
            ) : null}
          </PfMap>
        </Box>
      </Box>

      <Box className={`${mobileClasses.divider} ${mobileClasses.sectionPad}`} py="md">
        <Stack gap="md">
          <ProximityIndicator
            isCloseEnough={isCloseEnough}
            proximity={proximityUi}
          />

          <Stack gap="xs">
            <Box className={classes.captureRoot}>
              <Button
                aria-hidden={canCapture}
                className={mobileClasses.touchButton}
                disabled={!canCapture}
                fullWidth
                leftSection={<IconCamera size={22} />}
                loading={isSubmitting}
                size="lg"
                tabIndex={canCapture ? -1 : 0}
              >
                {isSubmitting ? 'Envoi en cours…' : 'Prendre et envoyer la preuve'}
              </Button>
              {canCapture ? (
                <input
                  {...photoInputProps}
                  aria-label="Prendre et envoyer la preuve"
                  className={classes.captureInputOverlay}
                  id={photoInputId}
                  onChange={handleFileChange}
                  type="file"
                />
              ) : null}
            </Box>
            {!canCapture && !isSubmitting ? (
              <Text c="dimmed" size="sm" ta="center">
                {isAlreadySubmitted
                  ? 'La preuve de cette parcelle a déjà été envoyée.'
                  : hasGps
                    ? 'Rapprochez-vous de la parcelle pour activer l’appareil photo.'
                    : 'Activez votre position pour continuer.'}
              </Text>
            ) : isCloseEnough && !isSubmitting ? (
              <Text c="dimmed" size="sm" ta="center">
                L’appareil photo s’ouvre directement. Validez la photo pour l’envoyer.
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
